/**
 * Admin -> Edge Fleet -> Rollout controls (release 2.1 items 24 and 22).
 *
 * Three things an operator rolling an agent out to thousands of laptops needs
 * in one place, next to the install tokens:
 *
 *   - FLEET-WIDE PAUSE. One act stops every device scanning at its next
 *     check-in. It is behind a typed confirmation because it is the one button
 *     here that reaches every laptop in the company. Resume takes it back.
 *   - BUSINESS UNIT PAUSE. The same, for one unit. (A single wave is paused
 *     from its token row above.) A pause from any scope wins over everything
 *     below it: resuming a unit does not resume a wave or a device that has its
 *     own pause.
 *   - AGENT VERSIONS. How many devices run which version, by unit and wave; a
 *     target version per unit or wave; and the devices behind their target.
 *     A target is DESCRIPTIVE: agent updates reach laptops through Intune, and
 *     setting a target sends nothing to any device.
 *
 * Release 3.1: each active pause says how far it has reached - "N of M
 * confirmed paused", and why the rest have not confirmed yet - and the page
 * re-reads the pauses every 10 s until every device has confirmed.
 *
 * Release 3.1 wave 2a: AGENT TYPE PAUSE. Every agent of one type (every DAM
 * collector, all agentless collection, every decision gateway) in one act,
 * behind a typed confirmation like the fleet pause, with what the pause does
 * to that type said before the button - and, for gateways, a warning: a paused
 * gateway applies its fail policy, and closed refuses customer SQL. The
 * progress also counts the agents the controller enforces the pause on
 * (nothing for them to confirm) and the ones nothing would stop.
 *
 * Backend:
 *   GET  /v1/edge/rollout/scope-overrides    (each row carries `progress`, `scopeKey`)
 *   POST /v1/edge/rollout/scope-overrides   { scopeType, scopeId?, scopeKey?, paused, reason }
 *   GET  /v1/edge/fleet/versions
 *   PUT  /v1/edge/fleet/version-targets     { scopeType, scopeId, targetVersion | null, reason }
 */
import { Rx, dpdpaText, useRegulationTermsMode, useRegulationText, type RegulationTermsMode } from '@/lib/regulation-terms';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Loader2, OctagonPause, Play, RefreshCw, Target } from 'lucide-react';

import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  getEdgeFleetVersions,
  listBusinessUnits,
  listEdgeInstallTokens,
  listEdgeScopeOverrides,
  problemDetail,
  setEdgeScopePause,
  setEdgeVersionTarget,
  type EdgeAgentType,
  type EdgeFleetVersions,
  type EdgeScopeOverride,
  type EdgeScopePropagation,
} from '@/lib/api';
import {
  AGENT_TYPE_PAUSE_EFFECT,
  EDGE_AGENT_TYPES,
  EDGE_AGENT_TYPE_LABEL,
  EDGE_AGENT_TYPE_PLURAL,
  inSentence,
  isEdgeAgentType,
} from '@/lib/edge-agent-types';

/** The exact words the operator types to pause every device. */
export const FLEET_PAUSE_CONFIRMATION = 'PAUSE ALL';

/**
 * The exact words the operator types to pause every agent of one type
 * ("PAUSE DAM COLLECTORS", "PAUSE DECISION GATEWAYS"): the type is in the
 * words, so a confirmation typed for one type cannot pause another.
 */
export function agentTypePauseConfirmation(t: EdgeAgentType, mode: RegulationTermsMode = 'legacy'): string {
  return dpdpaText(`PAUSE ${EDGE_AGENT_TYPE_PLURAL[t].toUpperCase()}`, mode);
}

/** How often the pauses are re-read while one has not reached every device. */
export const PAUSE_PROGRESS_POLL_MS = 10_000;

/** True while a pause has devices that have not confirmed it yet. */
export function pauseStillSpreading(o: EdgeScopeOverride | null | undefined): boolean {
  return !!o && o.paused && !!o.progress && o.progress.confirmed < o.progress.devices;
}

/** "1,180 of 1,200 confirmed paused". */
export function pauseProgressHeadline(p: EdgeScopePropagation): string {
  const n = (x: number) => x.toLocaleString('en-IN');
  return `${n(p.confirmed)} of ${n(p.devices)} confirmed paused`;
}

/**
 * How far one pause has reached the devices it covers. A device confirms by
 * reporting the pause back on a check-in after it was set; the rest are shown
 * by why they have not, because "waiting for a laptop that is asleep" and
 * "a laptop that checked in and ignored it" call for different actions.
 */
export function PauseProgress({ progress }: { progress: EdgeScopePropagation | undefined }) {
  // A controller before release 3.1 does not report progress: say nothing
  // rather than a number we do not have.
  if (!progress) return null;
  const n = (x: number) => x.toLocaleString('en-IN');
  const done = progress.devices > 0 && progress.confirmed >= progress.devices;
  const enforced = progress.enforcedByController ?? 0;
  return (
    <div className="mt-1 space-y-0.5 text-xs" data-testid="pause-progress">
      {progress.devices === 0 ? (
        enforced > 0 ? null : (
          <p className="text-muted-foreground">No agent that reads a pause itself is running in this scope yet.</p>
        )
      ) : (
        <p className={done ? 'text-foreground' : 'text-foreground font-medium'}>
          {pauseProgressHeadline(progress)}
          {done ? '.' : ' so far.'}
        </p>
      )}
      {progress.devices > 0 && !done ? (
        <ul className="list-disc pl-5 text-muted-foreground">
          <li>
            {n(progress.checkedInNotConfirmed)} checked in but not yet confirmed (most confirm on their
            following check-in; an agent older than 2.1 never reports it)
          </li>
          <li>{n(progress.pendingOnline)} waiting for their next check-in</li>
          <li>
            {n(progress.pendingOffline)} offline: they learn it when they come back, and their findings are
            refused meanwhile
          </li>
        </ul>
      ) : null}
      {enforced > 0 ? (
        <p className="text-foreground" data-testid="pause-enforced-by-controller">
          {n(enforced)} enforced by the controller (decision gateways, <Rx>DSAR</Rx> connectors, agentless collection): the
          pause is in force from their next call, and they have nothing to confirm.
        </p>
      ) : null}
      {progress.ignoresPause > 0 ? (
        <p className="text-warning" data-testid="pause-ignored">
          {n(progress.ignoresPause)} in this scope keep running: nothing stops a DAM collector older than 3.1 or a
          gateway with no service credential on record. Upgrade them, or stop them in their own deployment.
        </p>
      ) : null}
    </div>
  );
}

const INPUT_CLASS = 'rounded border border-border/40 bg-background/40 px-2 py-1.5 text-sm text-foreground';

/**
 * A fleet or unit pause reaches every agent type in it (release 3.1 wave 2a),
 * decision gateways included: the controller refuses a paused gateway's
 * decision calls, and the gateway applies its fail policy. Said where the
 * pause is set, because "pause the fleet" reads like "stop scanning".
 */
export function ScopeGatewayNote() {
  return (
    <p className="flex items-start gap-1.5 text-xs text-foreground" data-testid="scope-gateway-note">
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning" />
      <span>
        This also pauses the <Rx>DSAR</Rx> connectors and agentless collection in it. It does not pause the decision
        gateways: a paused gateway applies its fail policy, and with fail policy closed it refuses customer SQL.
        Pause gateways by type below, or one at a time from its row.
      </span>
    </p>
  );
}

function when(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString() : 'never';
}

export function FleetRolloutControls() {
  const mode = useRegulationTermsMode();
  const tx = useRegulationText();
  const [overrides, setOverrides] = useState<EdgeScopeOverride[] | null>(null);
  const [versions, setVersions] = useState<EdgeFleetVersions | null>(null);
  const [units, setUnits] = useState<Array<{ id: string; name: string }>>([]);
  const [tokenIds, setTokenIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Fleet-wide pause form.
  const [confirmText, setConfirmText] = useState('');
  const [fleetReason, setFleetReason] = useState('');

  // Business-unit pause form.
  const [unitToPause, setUnitToPause] = useState('');
  const [unitReason, setUnitReason] = useState('');

  // Agent-type pause form (release 3.1 wave 2a).
  const [typeToPause, setTypeToPause] = useState<EdgeAgentType | ''>('');
  const [typeConfirm, setTypeConfirm] = useState('');
  const [typeReason, setTypeReason] = useState('');

  // Version target form.
  const [targetScope, setTargetScope] = useState<'business_unit' | 'install_token'>('install_token');
  const [targetId, setTargetId] = useState('');
  const [targetVersion, setTargetVersion] = useState('');
  const [targetReason, setTargetReason] = useState('');

  const load = useCallback(async () => {
    setError(null);
    try {
      const [o, v] = await Promise.all([listEdgeScopeOverrides(), getEdgeFleetVersions()]);
      setOverrides(o.scopeOverrides);
      setVersions(v);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'could not load rollout controls');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Names for units and the token ids for the pickers. A failure leaves ids
  // showing; the controls still work by id.
  useEffect(() => {
    let cancelled = false;
    listBusinessUnits()
      .then((r) => {
        if (!cancelled) setUnits(r.businessUnits.map((b) => ({ id: b.id, name: b.name })));
      })
      .catch(() => undefined);
    listEdgeInstallTokens()
      .then((r) => {
        if (!cancelled) setTokenIds(r.tokens.filter((t) => t.revokedAt === null).map((t) => t.id));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const unitName = useMemo(() => {
    const m = new Map(units.map((u) => [u.id, u.name]));
    return (id: string | null) => (id === null ? 'No unit' : (m.get(id) ?? id.slice(0, 8)));
  }, [units]);

  const fleetPause = overrides?.find((o) => o.scopeType === 'tenant' && o.paused) ?? null;
  const pausedUnits = (overrides ?? []).filter((o) => o.scopeType === 'business_unit' && o.paused);
  const pausedTypes = (overrides ?? []).filter((o) => o.scopeType === 'agent_type' && o.paused);
  const typeName = (o: EdgeScopeOverride) =>
    isEdgeAgentType(o.scopeKey) ? tx(EDGE_AGENT_TYPE_LABEL[o.scopeKey]) : (o.scopeKey ?? 'an agent type');

  // While a pause on screen has not reached every device, re-read the pauses
  // (only them - the versions view does not move with a pause) every 10 s so
  // "N of M confirmed" counts up without a reload. Once every one is
  // confirmed the timer stops and the page loads as it always did.
  const spreading =
    pauseStillSpreading(fleetPause) ||
    pausedUnits.some((o) => pauseStillSpreading(o)) ||
    pausedTypes.some((o) => pauseStillSpreading(o));
  useEffect(() => {
    if (!spreading) return;
    const t = setInterval(() => {
      listEdgeScopeOverrides()
        .then((o) => setOverrides(o.scopeOverrides))
        .catch(() => undefined); // keep the last answer; the next tick tries again
    }, PAUSE_PROGRESS_POLL_MS);
    return () => clearInterval(t);
  }, [spreading]);

  const act = async (fn: () => Promise<unknown>, done: string) => {
    setBusy(true);
    setNotice(null);
    try {
      await fn();
      setNotice(done);
      await load();
      return true;
    } catch (e) {
      // The server's own words (e.g. a scope the caller cannot pause).
      setNotice(problemDetail(e, 'the change was refused'));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const pauseFleet = async () => {
    const ok = await act(
      () => setEdgeScopePause({ scopeType: 'tenant', paused: true, reason: fleetReason.trim() }),
      'Every device stops scanning at its next check-in (within about 10 minutes).',
    );
    if (ok) {
      setConfirmText('');
      setFleetReason('');
    }
  };

  const resumeFleet = async () => {
    const ok = await act(
      () => setEdgeScopePause({ scopeType: 'tenant', paused: false, reason: fleetReason.trim() }),
      'The fleet pause is lifted. Devices held by a unit, wave or device pause stay paused.',
    );
    if (ok) setFleetReason('');
  };

  const pauseUnit = async () => {
    const ok = await act(
      () =>
        setEdgeScopePause({
          scopeType: 'business_unit',
          scopeId: unitToPause,
          paused: true,
          reason: unitReason.trim(),
        }),
      `${unitName(unitToPause)} paused. Its devices stop scanning at their next check-in.`,
    );
    if (ok) {
      setUnitToPause('');
      setUnitReason('');
    }
  };

  const resumeUnit = async (o: EdgeScopeOverride) => {
    const reason = window.prompt(`Resume ${unitName(o.scopeId)}?\n\nReason (recorded in the audit chain):`);
    if (reason === null) return;
    if (reason.trim().length < 3) {
      setNotice('Resume needs a reason of at least 3 characters.');
      return;
    }
    await act(
      () =>
        setEdgeScopePause({
          scopeType: 'business_unit',
          scopeId: o.scopeId ?? undefined,
          paused: false,
          reason: reason.trim(),
        }),
      `${unitName(o.scopeId)} resumed.`,
    );
  };

  const pauseType = async () => {
    if (!typeToPause) return;
    const t = typeToPause;
    const ok = await act(
      () => setEdgeScopePause({ scopeType: 'agent_type', scopeKey: t, paused: true, reason: typeReason.trim() }),
      tx(`Every ${inSentence(EDGE_AGENT_TYPE_LABEL[t])} is paused. ${AGENT_TYPE_PAUSE_EFFECT[t]}`),
    );
    if (ok) {
      setTypeToPause('');
      setTypeConfirm('');
      setTypeReason('');
    }
  };

  const resumeType = async (o: EdgeScopeOverride) => {
    if (!isEdgeAgentType(o.scopeKey)) return;
    const t = o.scopeKey;
    const reason = window.prompt(tx(`Resume every ${inSentence(EDGE_AGENT_TYPE_LABEL[t])}?\n\nReason (recorded in the audit chain):`));
    if (reason === null) return;
    if (reason.trim().length < 3) {
      setNotice('Resume needs a reason of at least 3 characters.');
      return;
    }
    await act(
      () => setEdgeScopePause({ scopeType: 'agent_type', scopeKey: t, paused: false, reason: reason.trim() }),
      tx(`Every ${inSentence(EDGE_AGENT_TYPE_LABEL[t])} is resumed. Agents held by a fleet, unit, wave or device pause stay paused.`),
    );
  };

  const saveTarget = async () => {
    const ok = await act(
      () =>
        setEdgeVersionTarget({
          scopeType: targetScope,
          scopeId: targetId,
          targetVersion: targetVersion.trim(),
          reason: targetReason.trim(),
        }),
      `Target set to ${targetVersion.trim()}. Nothing was sent to any device.`,
    );
    if (ok) {
      setTargetVersion('');
      setTargetReason('');
    }
  };

  const clearTarget = async (scopeType: 'business_unit' | 'install_token', scopeId: string) => {
    const reason = window.prompt('Clear this target version?\n\nReason (recorded in the audit chain):');
    if (reason === null) return;
    if (reason.trim().length < 3) {
      setNotice('Clearing a target needs a reason of at least 3 characters.');
      return;
    }
    await act(
      () => setEdgeVersionTarget({ scopeType, scopeId, targetVersion: null, reason: reason.trim() }),
      'Target cleared.',
    );
  };

  const scopeLabel = (scopeType: 'business_unit' | 'install_token', id: string | null) =>
    scopeType === 'install_token' ? `Wave ${id?.slice(0, 8) ?? ''}` : `Unit ${unitName(id)}`;

  // Devices per version, summed over units and waves, newest version first.
  const versionTotals = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of versions?.counts ?? []) m.set(c.version, (m.get(c.version) ?? 0) + c.count);
    return [...m.entries()];
  }, [versions]);

  const versionValid = /^[0-9]+(\.[0-9]+)*$/.test(targetVersion.trim());

  return (
    <GlassCard className="mt-4 p-0">
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="flex items-center gap-2">
            <OctagonPause className="h-4 w-4" />
            Rollout controls
          </CardTitle>
          <CardDescription>
            Pause the whole fleet, one business unit or every agent of one type, and see which agent
            version each device runs against the version it should be on. Pauses reach each agent at
            its next check-in (or, for gateways, <Rx>DSAR</Rx> connectors and agentless collection, its next call
            to the controller); nothing is uninstalled.
          </CardDescription>
        </div>
        <Button variant="ghost" size="sm" onClick={() => void load()} aria-label="Reload rollout controls">
          <RefreshCw className="h-4 w-4" />
        </Button>
      </CardHeader>

      <div className="space-y-6 px-6 pb-6">
        {error ? <p className="text-sm text-destructive">{error}</p> : null}
        {overrides === null && !error ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading...
          </p>
        ) : null}

        {overrides !== null ? (
          <section aria-labelledby="fleet-pause-heading" className="space-y-2">
            <h3 id="fleet-pause-heading" className="text-sm font-semibold text-foreground">
              Whole fleet
            </h3>
            {fleetPause ? (
              <div className="space-y-2 rounded-md border border-warning/30 bg-warning/10 p-3">
                <p className="text-sm text-foreground">
                  Every device is paused, since {when(fleetPause.createdAt)} by{' '}
                  {fleetPause.pushedByActorId}: {fleetPause.reason}
                </p>
                <PauseProgress progress={fleetPause.progress} />
                <div className="flex flex-wrap items-end gap-3">
                  <div className="min-w-48 flex-1">
                    <label htmlFor="fleet-resume-reason" className="mb-1 block text-xs text-muted-foreground">
                      Reason for resuming (recorded in the audit log)
                    </label>
                    <input
                      id="fleet-resume-reason"
                      className={`w-full ${INPUT_CLASS}`}
                      value={fleetReason}
                      maxLength={500}
                      onChange={(e) => setFleetReason(e.target.value)}
                    />
                  </div>
                  <Button size="sm" disabled={busy || fleetReason.trim().length < 3} onClick={() => void resumeFleet()} data-action="disc.adm_edge_fleet.a13">
                    <Play className="mr-1 h-3 w-3" /> Resume the fleet
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-2 rounded-md border border-border/40 bg-muted/20 p-3">
                <p className="text-sm text-muted-foreground">
                  Stops every device in the company scanning. Type{' '}
                  <span className="font-mono text-foreground">{FLEET_PAUSE_CONFIRMATION}</span> to confirm.
                </p>
                <ScopeGatewayNote />
                <div className="flex flex-wrap items-end gap-3">
                  <div>
                    <label htmlFor="fleet-pause-confirm" className="mb-1 block text-xs text-muted-foreground">
                      Confirmation
                    </label>
                    <input
                      id="fleet-pause-confirm"
                      className={`w-36 font-mono ${INPUT_CLASS}`}
                      value={confirmText}
                      autoComplete="off"
                      onChange={(e) => setConfirmText(e.target.value)}
                    />
                  </div>
                  <div className="min-w-48 flex-1">
                    <label htmlFor="fleet-pause-reason" className="mb-1 block text-xs text-muted-foreground">
                      Reason (recorded in the audit log)
                    </label>
                    <input
                      id="fleet-pause-reason"
                      className={`w-full ${INPUT_CLASS}`}
                      value={fleetReason}
                      maxLength={500}
                      onChange={(e) => setFleetReason(e.target.value)}
                    />
                  </div>
                  <Button
                    size="sm"
                    variant="destructive"
                    disabled={busy || confirmText !== FLEET_PAUSE_CONFIRMATION || fleetReason.trim().length < 3}
                    onClick={() => void pauseFleet()} data-action="disc.adm_edge_fleet.a12"
                  >
                    <OctagonPause className="mr-1 h-3 w-3" /> Pause every device
                  </Button>
                </div>
              </div>
            )}
          </section>
        ) : null}

        {overrides !== null ? (
          <section aria-labelledby="unit-pause-heading" className="space-y-2">
            <h3 id="unit-pause-heading" className="text-sm font-semibold text-foreground">
              Business units
            </h3>
            {pausedUnits.length === 0 ? (
              <p className="text-sm text-muted-foreground">No business unit is paused.</p>
            ) : (
              <ul className="space-y-1">
                {pausedUnits.map((o) => (
                  <li key={o.id} className="text-sm">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="text-foreground">
                        <span className="mr-2 rounded border border-warning/30 bg-warning/15 px-2 py-0.5 text-xs text-warning">
                          Paused
                        </span>
                        {unitName(o.scopeId)}, since {when(o.createdAt)}: {o.reason}
                      </span>
                      <Button variant="ghost" size="sm" disabled={busy} onClick={() => void resumeUnit(o)} data-action="disc.adm_edge_fleet.a15">
                        <Play className="mr-1 h-3 w-3" /> Resume
                      </Button>
                    </div>
                    <PauseProgress progress={o.progress} />
                  </li>
                ))}
              </ul>
            )}
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label htmlFor="unit-pause-select" className="mb-1 block text-xs text-muted-foreground">
                  Unit
                </label>
                <select
                  id="unit-pause-select"
                  className={INPUT_CLASS}
                  value={unitToPause}
                  onChange={(e) => setUnitToPause(e.target.value)}
                >
                  <option value="">Choose a unit</option>
                  {units
                    .filter((u) => !pausedUnits.some((o) => o.scopeId === u.id))
                    .map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name}
                      </option>
                    ))}
                </select>
              </div>
              <div className="min-w-48 flex-1">
                <label htmlFor="unit-pause-reason" className="mb-1 block text-xs text-muted-foreground">
                  Reason (recorded in the audit log)
                </label>
                <input
                  id="unit-pause-reason"
                  className={`w-full ${INPUT_CLASS}`}
                  value={unitReason}
                  maxLength={500}
                  onChange={(e) => setUnitReason(e.target.value)}
                />
              </div>
              <Button
                size="sm"
                variant="ghost"
                disabled={busy || unitToPause === '' || unitReason.trim().length < 3}
                onClick={() => void pauseUnit()} data-action="disc.adm_edge_fleet.a14"
              >
                <OctagonPause className="mr-1 h-3 w-3" /> Pause unit
              </Button>
            </div>
            {unitToPause ? <ScopeGatewayNote /> : null}
          </section>
        ) : null}

        {overrides !== null ? (
          <section aria-labelledby="type-pause-heading" className="space-y-2" data-testid="agent-type-pause">
            <h3 id="type-pause-heading" className="text-sm font-semibold text-foreground">
              Agent types
            </h3>
            {pausedTypes.length === 0 ? (
              <p className="text-sm text-muted-foreground">No agent type is paused.</p>
            ) : (
              <ul className="space-y-1">
                {pausedTypes.map((o) => (
                  <li key={o.id} className="text-sm">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="text-foreground">
                        <span className="mr-2 rounded border border-warning/30 bg-warning/15 px-2 py-0.5 text-xs text-warning">
                          Paused
                        </span>
                        Every {inSentence(typeName(o))}, since {when(o.createdAt)}: {o.reason}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={busy || !isEdgeAgentType(o.scopeKey)}
                        onClick={() => void resumeType(o)}
                        aria-label={`Resume every ${inSentence(typeName(o))}`}
                      >
                        <Play className="mr-1 h-3 w-3" /> Resume
                      </Button>
                    </div>
                    <PauseProgress progress={o.progress} />
                  </li>
                ))}
              </ul>
            )}
            <div className="space-y-2 rounded-md border border-border/40 bg-muted/20 p-3">
              <div className="flex flex-wrap items-end gap-3">
                <div>
                  <label htmlFor="type-pause-select" className="mb-1 block text-xs text-muted-foreground">
                    Agent type
                  </label>
                  <select
                    id="type-pause-select"
                    className={INPUT_CLASS}
                    value={typeToPause}
                    onChange={(e) => {
                      setTypeToPause(e.target.value as EdgeAgentType | '');
                      setTypeConfirm('');
                    }}
                  >
                    <option value="">Choose a type</option>
                    {EDGE_AGENT_TYPES.filter((t) => !pausedTypes.some((o) => o.scopeKey === t)).map((t) => (
                      <option key={t} value={t}>
                        {tx(EDGE_AGENT_TYPE_LABEL[t])}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {typeToPause ? (
                <>
                  <p className="text-sm text-foreground" data-testid="type-pause-effect">
                    {tx(AGENT_TYPE_PAUSE_EFFECT[typeToPause])}
                  </p>
                  {typeToPause === 'gateway' ? (
                    <div
                      className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-foreground"
                      role="alert"
                      data-testid="gateway-pause-warning"
                    >
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                      <span>
                        <strong>This can stop customer traffic.</strong> A paused gateway applies its fail policy.
                        A gateway with fail policy <span className="font-mono">closed</span> (the default) refuses
                        every customer SQL statement it would have checked until you resume it; one with{' '}
                        <span className="font-mono">degraded</span> lets them through unchecked and records each
                        one. Each gateway&apos;s fail policy is in its drawer.
                      </span>
                    </div>
                  ) : null}
                  <p className="text-sm text-muted-foreground">
                    Type <span className="font-mono text-foreground">{agentTypePauseConfirmation(typeToPause, mode)}</span>{' '}
                    to confirm.
                  </p>
                  <div className="flex flex-wrap items-end gap-3">
                    <div>
                      <label htmlFor="type-pause-confirm" className="mb-1 block text-xs text-muted-foreground">
                        Type confirmation
                      </label>
                      <input
                        id="type-pause-confirm"
                        className={`w-64 font-mono ${INPUT_CLASS}`}
                        value={typeConfirm}
                        autoComplete="off"
                        onChange={(e) => setTypeConfirm(e.target.value)}
                      />
                    </div>
                    <div className="min-w-48 flex-1">
                      <label htmlFor="type-pause-reason" className="mb-1 block text-xs text-muted-foreground">
                        Reason (recorded in the audit log)
                      </label>
                      <input
                        id="type-pause-reason"
                        className={`w-full ${INPUT_CLASS}`}
                        value={typeReason}
                        maxLength={500}
                        onChange={(e) => setTypeReason(e.target.value)}
                      />
                    </div>
                    <Button
                      size="sm"
                      variant="destructive"
                      disabled={
                        busy ||
                        typeConfirm !== agentTypePauseConfirmation(typeToPause, mode) ||
                        typeReason.trim().length < 3
                      }
                      onClick={() => void pauseType()}
                    >
                      <OctagonPause className="mr-1 h-3 w-3" /> Pause every {tx(inSentence(EDGE_AGENT_TYPE_LABEL[typeToPause]))}
                    </Button>
                  </div>
                </>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Choose a type to see what a pause does to it. A pause from any scope wins: resuming a type does
                  not resume agents a fleet, unit, wave or device pause still holds.
                </p>
              )}
            </div>
          </section>
        ) : null}

        {versions !== null ? (
          <section aria-labelledby="versions-heading" className="space-y-3">
            <h3 id="versions-heading" className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <Target className="h-4 w-4" /> Agent versions
            </h3>
            <p className="text-xs text-muted-foreground">
              Updates reach laptops through Intune. A target version here only marks which devices are
              behind; it sends nothing to any device.
            </p>

            {versionTotals.length === 0 ? (
              <p className="text-sm text-muted-foreground">No running devices yet.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {versionTotals.map(([v, n]) => (
                  <span key={v} className="rounded border border-border/40 px-2 py-1 text-xs text-foreground">
                    <span className="font-mono">{v}</span>
                    <span className="ml-2 tabular-nums text-muted-foreground">
                      {n} device{n === 1 ? '' : 's'}
                    </span>
                  </span>
                ))}
              </div>
            )}

            {versions.counts.length > 0 ? (
              <details>
                <summary className="cursor-pointer text-xs text-muted-foreground">
                  By version, unit and wave ({versions.counts.length} rows)
                </summary>
                <div className="mt-2 max-h-64 overflow-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                        <th className="py-1 pr-4">Version</th>
                        <th className="py-1 pr-4">Business unit</th>
                        <th className="py-1 pr-4">Wave</th>
                        <th className="py-1 pr-4">Devices</th>
                      </tr>
                    </thead>
                    <tbody>
                      {versions.counts.map((c) => (
                        <tr
                          key={`${c.version}|${c.businessUnitId ?? ''}|${c.installTokenId ?? ''}`}
                          className="border-t border-border/40"
                        >
                          <td className="py-1 pr-4 font-mono text-xs text-foreground">{c.version}</td>
                          <td className="py-1 pr-4 text-xs text-foreground">{unitName(c.businessUnitId)}</td>
                          <td className="py-1 pr-4 font-mono text-xs text-foreground">
                            {c.installTokenId ? c.installTokenId.slice(0, 8) : 'none'}
                          </td>
                          <td className="py-1 pr-4 tabular-nums text-foreground">{c.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            ) : null}

            <div className="space-y-1">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Targets</h4>
              {versions.targets.length === 0 ? (
                <p className="text-sm text-muted-foreground">No target versions set.</p>
              ) : (
                <ul className="space-y-1">
                  {versions.targets.map((t) => (
                    <li
                      key={`${t.scopeType}|${t.scopeId}`}
                      className="flex flex-wrap items-center justify-between gap-2 text-sm"
                    >
                      <span className="text-foreground">
                        {scopeLabel(t.scopeType, t.scopeId)} should run{' '}
                        <span className="font-mono">{t.targetVersion}</span>
                        <span className="ml-2 text-xs text-muted-foreground">
                          set {when(t.createdAt)}: {t.reason}
                        </span>
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={busy}
                        onClick={() => void clearTarget(t.scopeType, t.scopeId)} data-action="disc.adm_edge_fleet.a17"
                      >
                        Clear
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
              <div className="flex flex-wrap items-end gap-3 pt-1">
                <div>
                  <label htmlFor="target-scope" className="mb-1 block text-xs text-muted-foreground">
                    For
                  </label>
                  <select
                    id="target-scope"
                    className={INPUT_CLASS}
                    value={targetScope}
                    onChange={(e) => {
                      setTargetScope(e.target.value as 'business_unit' | 'install_token');
                      setTargetId('');
                    }}
                  >
                    <option value="install_token">A wave</option>
                    <option value="business_unit">A business unit</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="target-id" className="mb-1 block text-xs text-muted-foreground">
                    {targetScope === 'install_token' ? 'Wave' : 'Unit'}
                  </label>
                  <select
                    id="target-id"
                    className={INPUT_CLASS}
                    value={targetId}
                    onChange={(e) => setTargetId(e.target.value)}
                  >
                    <option value="">Choose</option>
                    {targetScope === 'install_token'
                      ? tokenIds.map((id) => (
                          <option key={id} value={id}>
                            Wave {id.slice(0, 8)}
                          </option>
                        ))
                      : units.map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.name}
                          </option>
                        ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="target-version" className="mb-1 block text-xs text-muted-foreground">
                    Version
                  </label>
                  <input
                    id="target-version"
                    className={`w-28 font-mono ${INPUT_CLASS}`}
                    value={targetVersion}
                    placeholder="0.0.12"
                    maxLength={32}
                    onChange={(e) => setTargetVersion(e.target.value)}
                  />
                </div>
                <div className="min-w-48 flex-1">
                  <label htmlFor="target-reason" className="mb-1 block text-xs text-muted-foreground">
                    Reason (recorded in the audit log)
                  </label>
                  <input
                    id="target-reason"
                    className={`w-full ${INPUT_CLASS}`}
                    value={targetReason}
                    maxLength={250}
                    onChange={(e) => setTargetReason(e.target.value)}
                  />
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={busy || targetId === '' || !versionValid || targetReason.trim().length < 3}
                  onClick={() => void saveTarget()} data-action="disc.adm_edge_fleet.a16"
                >
                  <Target className="mr-1 h-3 w-3" /> Set target
                </Button>
              </div>
            </div>

            <div className="space-y-1">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Behind target
              </h4>
              {versions.behindTotal === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {versions.targets.length === 0
                    ? 'Set a target to see which devices are behind it.'
                    : 'Every device with a target is on it or newer.'}
                </p>
              ) : (
                <>
                  <p className="text-sm text-foreground">
                    <strong>{versions.behindTotal}</strong> device{versions.behindTotal === 1 ? ' is' : 's are'}{' '}
                    behind their target
                    {versions.behindTotal > versions.behind.length
                      ? ` (showing the ${versions.behind.length} that checked in most recently)`
                      : ''}
                    .
                  </p>
                  <div className="max-h-64 overflow-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                          <th className="py-1 pr-4">Device</th>
                          <th className="py-1 pr-4">Runs</th>
                          <th className="py-1 pr-4">Target</th>
                          <th className="py-1 pr-4">Business unit</th>
                          <th className="py-1 pr-4">Last check-in</th>
                        </tr>
                      </thead>
                      <tbody>
                        {versions.behind.map((b) => (
                          <tr key={b.podId} className="border-t border-border/40">
                            <td className="py-1 pr-4 text-xs text-foreground">{b.displayName}</td>
                            <td className="py-1 pr-4 font-mono text-xs text-foreground">{b.version}</td>
                            <td className="py-1 pr-4 font-mono text-xs text-foreground">{b.targetVersion}</td>
                            <td className="py-1 pr-4 text-xs text-foreground">{unitName(b.businessUnitId)}</td>
                            <td className="py-1 pr-4 text-xs text-muted-foreground">{when(b.lastHeartbeatAt)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </section>
        ) : null}

        {notice ? <p className="text-sm text-foreground">{notice}</p> : null}
      </div>
    </GlassCard>
  );
}
